/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Fragment, forwardRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';

//types
import type {
    ComponentPropsWithRef,
    ComponentPropsWithoutRef,
    ElementType,
    MutableRefObject
} from 'react';

import Icon from '@/components/base/Icon';
import H2 from '@/components/base/Headings/H2';

const SIZE_DEFAULT = 'DEFAULT';
const SIZE_LG = 'lg';
const SIZE_XL = 'xl';
const SIZE_4XL = '4xl';
const SIZE_5XL = '5xl';
const SIZE_FULL = 'full';
const SIZE_FULL_SCHEDULER = 'full-scheduler';

const ModalSize = {
    [SIZE_DEFAULT]: 'sm:max-w-[37.5rem] my-8 rounded-md',
    [SIZE_LG]: 'sm:max-w-lg my-8 rounded-md',
    [SIZE_XL]: 'sm:max-w-2xl my-8 rounded-md',
    [SIZE_4XL]: 'sm:max-w-4xl my-8 rounded-md',
    [SIZE_5XL]: 'sm:max-w-5xl my-8 rounded-md',
    [SIZE_FULL_SCHEDULER]: 'w-screen flex-1 border border-white dark:border-icoDarkMode-anthracite',
    [SIZE_FULL]:
        'w-screen flex-1 mt-12 rounded-t-lg border border-white dark:border-icoDarkMode-anthracite'
} as const;

export const MODAL_DIALOG_CLASSNAME = 'modal-dialog';

export type ModalProps = ComponentPropsWithRef<'div'> & {
    size?: keyof typeof ModalSize;
    open: boolean;
    onDismiss?: () => void;
    withCloseButton?: boolean;
    overlayClosable?: boolean;
    scrollInside?: boolean;
    wrapperWidthFull?: boolean;
    scrollInsideWrapperClass?: string;
    initialFocus?: MutableRefObject<HTMLElement>;
    contentWrapperRef?: MutableRefObject<HTMLDivElement>; //Possibility to pass an external ref to the content to bind events like scroll to this context
};

const Modal = forwardRef<HTMLDivElement, ModalProps>(
    (
        {
            children,
            size = SIZE_DEFAULT,
            open,
            onDismiss,
            withCloseButton = true,
            overlayClosable = true,
            scrollInside = false,
            wrapperWidthFull = true,
            scrollInsideWrapperClass,
            className,
            contentWrapperRef,
            ...restProps
        },
        ref
    ) => {
        const { t } = useTranslation('commons');
        return (
            <Transition show={open} as={Fragment}>
                {/* You must have one focusable element inside your modal or 
                you should pass an initialFocus prop and add a ref to an element 
                onto an element*/}
                <Dialog
                    as="div"
                    ref={ref}
                    className={clsx(
                        'fixed inset-0 overflow-y-auto overflow-x-hidden',
                        {
                            'z-30': size !== SIZE_FULL && size !== SIZE_FULL_SCHEDULER,
                            'z-50': size === SIZE_FULL || size === SIZE_FULL_SCHEDULER
                        },
                        MODAL_DIALOG_CLASSNAME,
                        className
                    )}
                    static
                    open={open}
                    onClose={() => {
                        if (overlayClosable && typeof onDismiss === 'function') {
                            onDismiss();
                        }
                    }}
                    {...restProps}>
                    <div
                        className={clsx('text-center', {
                            'min-h-screen px-3': size !== SIZE_FULL && size !== SIZE_FULL_SCHEDULER,
                            'flex min-h-screen': size === SIZE_FULL || size === SIZE_FULL_SCHEDULER
                        })}>
                        {size !== 'full-scheduler' ? (
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0">
                                <div
                                    onClick={(e) => e.stopPropagation()}
                                    className={clsx('fixed inset-0', {
                                        'bg-icoBlack-400/70': size === SIZE_FULL,
                                        'bg-icoBlack-400/50 backdrop-blur-md': size !== SIZE_FULL
                                    })}
                                />
                            </Transition.Child>
                        ) : null}

                        {/* This element is to trick the browser into centering the modal contents. */}
                        <span className="inline-block h-screen align-middle" aria-hidden="true">
                            &#8203;
                        </span>
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom={clsx({
                                'opacity-0 scale-95': size !== SIZE_FULL,
                                'opacity-0 translate-x-10': size === SIZE_FULL
                            })}
                            enterTo={clsx({
                                'opacity-100 scale-100': size !== SIZE_FULL,
                                'opacity-100 translate-x-0': size === SIZE_FULL
                            })}
                            leave="ease-in duration-300"
                            leaveFrom={clsx({
                                'opacity-100 scale-200': size !== SIZE_FULL,
                                'opacity-100 translate-x-0': size === SIZE_FULL
                            })}
                            leaveTo={clsx({
                                'opacity-0 scale-95': size !== SIZE_FULL,
                                'opacity-0 translate-x-10': size === SIZE_FULL
                            })}>
                            <Dialog.Panel
                                className={clsx(
                                    'relative inline-block bg-white text-left align-middle transition-all dark:bg-icoDarkMode-anthracite',
                                    ModalSize[size],
                                    {
                                        'w-full': wrapperWidthFull,
                                        'max-h-[calc(100vh-48px)]': size === SIZE_FULL
                                    }
                                )}>
                                {size === SIZE_FULL ? (
                                    <div className="h-full overflow-hidden rounded-t-lg">
                                        <div className="scrollbar h-full" ref={contentWrapperRef}>
                                            <div className="flex h-min min-h-full w-full">
                                                {children}
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                                {scrollInside ? (
                                    <div className={scrollInsideWrapperClass}>{children}</div>
                                ) : null}
                                {size !== SIZE_FULL && !scrollInside ? <>{children}</> : null}
                                {/* Absolutely position close button👇 */}
                                {withCloseButton ? (
                                    <button
                                        type="button"
                                        aria-label={t('close')}
                                        className={clsx(
                                            'absolute inline-flex items-center justify-center rounded-full border p-4 shadow-icoXL backdrop-blur-md',
                                            // Colors
                                            'border-white bg-white/80 dark:border-icoDarkMode-deepBlack dark:bg-icoDarkMode-deepBlack/40',
                                            // Firefox
                                            'firefox:bg-gradient-to-tr firefox:from-icoGrayFirefox-300 firefox:to-icoWhiteFirefox dark:firefox:bg-icoDarkMode-deepBlack dark:firefox:bg-opacity-90 dark:firefox:bg-none',
                                            {
                                                '-right-2 -top-2 sm:-right-6 sm:-top-6':
                                                    size !== SIZE_FULL,
                                                '-top-6 right-8': size === SIZE_FULL
                                            }
                                        )}
                                        onClick={onDismiss}>
                                        <Icon icon="close" className="text-sm text-icoGray-500" />
                                    </button>
                                ) : null}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition>
        );
    }
);

Modal.displayName = 'Modal';

// Modal Body 👇
const DefaultBodyStyle = 'px-5 sm:px-12 pt-12';

type BodyProps = ComponentPropsWithRef<'div'>;
const Body = forwardRef<HTMLDivElement, BodyProps>(({ className, ...restProps }, ref) => (
    <div className={className ?? DefaultBodyStyle} ref={ref} {...restProps} />
));

Body.displayName = 'Modal.Body';

//Modal Title 👇
const DefaultTitleStyle = 'text-center';

type TitleProps = ComponentPropsWithoutRef<'h2'>;
const Title = ({ className, ...restProps }: TitleProps) => (
    <Dialog.Title as={H2} className={className ?? DefaultTitleStyle} {...restProps} />
);

// Modal Content 👇
const DefaultContentStyle = 'py-m';

export type ContentProps = ComponentPropsWithoutRef<'div'>;
const Content = ({ className, ...restProps }: ContentProps) => (
    <Dialog.Description as="div" className={className ?? DefaultContentStyle} {...restProps} />
);

// Modal Footer 👇
const DefaultFooterStyle = 'flex items-center justify-between px-5 sm:px-12 pb-12';
type FooterProps = ComponentPropsWithoutRef<'div'> & {
    as?: ElementType;
    isRow?: boolean;
    divider?: boolean;
};
const Footer = ({
    className,
    as: Component = 'div',
    isRow = true,
    divider = false,
    ...restProps
}: FooterProps) => {
    return (
        <>
            {divider ? (
                <hr className="border-t-1 mt-12 border-icoGray-300 dark:border-icoDarkMode-beton" />
            ) : null}
            <Component
                className={
                    className ??
                    clsx(DefaultFooterStyle, {
                        'flex-col space-y-m': !isRow,
                        'pt-m': divider,
                        'pt-8': !divider
                    })
                }
                {...restProps}
            />
        </>
    );
};

const ModalCompound = Object.assign({}, Modal, {
    Body,
    Title,
    Content,
    Footer
});

export default ModalCompound;
